import { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { AgentStatus } from "@/features/chat/types/sdk";
import type { Message } from "@/features/chat/types/sdk";
import { convertFirebaseMessages, type FirebaseMessage } from "@/features/chat/lib/message-converter";
import { useStreamActivityGuard } from "./useStreamActivityGuard";

interface UseRealtimeMessagesResult {
  isListening: boolean;
}

/**
 * Hook to subscribe to messages when agent is active BUT SSE stream is not active.
 * Uses functional setState to avoid race conditions with SSE stream.
 *
 * Activates when:
 * - agentStatus is an "active" status (RUNNING_AGENT_LOOP, WAITING_ON_ANSWER)
 * - AND hasActiveStream === false (no SSE connection)
 * - AND we haven't had an active stream this session (page reload scenario)
 *
 * This ensures onSnapshot runs during page reload (reconnect) to sync messages,
 * and continues listening until SSE stream reconnects or agent becomes idle.
 *
 * IMPORTANT: Firebase is the source of truth for persisted messages.
 * In reconnect mode, we completely replace local state with Firestore data.
 */
export function useRealtimeMessages(
  userId: string | null,
  projectId: string | null,
  agentStatus: AgentStatus | null,
  updateMessages: (updater: (prev: Message[]) => Message[]) => void,
  hasActiveStream: boolean
): UseRealtimeMessagesResult {
  const [isListening, setIsListening] = useState(false);

  // Use shared guard to determine if we should subscribe
  const { shouldSubscribe } = useStreamActivityGuard(hasActiveStream);

  useEffect(() => {
    // Active statuses where we should listen for message updates
    const activeStatuses: AgentStatus[] = [
      'RUNNING_AGENT_LOOP',
      'WAITING_ON_ANSWER',
    ];

    const isAgentActive = agentStatus && activeStatuses.includes(agentStatus);

    // Only listen when agent is active AND guard allows subscription
    if (!isAgentActive || !userId || !projectId || !shouldSubscribe) {
      setIsListening(false);
      return;
    }

    setIsListening(true);
    console.log(`[useRealtimeMessages] ▶️ Listening for messages (page reload reconnect)`);

    // Subscribe to messages subcollection
    const messagesRef = collection(
      db(),
      "users",
      userId,
      "projects",
      projectId,
      "messages"
    );
    const q = query(messagesRef, orderBy("created_at", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // Get raw Firestore documents
        const rawMessages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as FirebaseMessage[];

        // Convert Firebase messages to frontend Message format
        const firestoreMessages = convertFirebaseMessages(rawMessages);

        // In reconnect mode, Firebase is the ONLY source of truth
        updateMessages((prev) => {
          const sortedMessages = [...firestoreMessages].sort((a, b) => {
            const aTime = new Date(a.created_at || 0).getTime();
            const bTime = new Date(b.created_at || 0).getTime();
            return aTime - bTime;
          });

          // Only log when count changes
          if (sortedMessages.length !== prev.length) {
            console.log(`[useRealtimeMessages] 📥 ${prev.length} → ${sortedMessages.length} messages`);
          }

          return sortedMessages;
        });
      },
      (err) => {
        console.error("Error listening to messages:", err);
        setIsListening(false);
      }
    );

    return () => {
      unsubscribe();
      setIsListening(false);
    };
  }, [userId, projectId, agentStatus, updateMessages, shouldSubscribe]);

  return { isListening };
}
